import MilestoneHelper from './Milestones/MilestoneHelper';
import ChartProps from '../../Charts/ChartProps';
import * as DateUtil from '../../lib/dateutils';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Icons from '../../ui/IconButton/Icons';
import Help from '../../ui/Help/Help';
import Lead from '../../entities/lead';
import LeadHelper from './LeadHelper';
import {Tooltip} from "@mui/material";
import {connect} from "react-redux";
import type {RootState} from "../../@types/store";
import {useMemo} from "react";

function LeadStage(props: LeadStageProps) {
    const lead = props.lead;
    const milestoneDate = useMemo(() => LeadHelper.getLatestMilestone(lead)?.dateCompleted, [lead]);

    /* eslint-disable react-hooks/error-boundaries -- Intentional defensive try/catch around synchronous render computation (milestone math/date formatting); child render-phase errors fall through to the app-level ErrorBoundaryO (src/index.tsx). */
    try {
        if (!lead) {
            console.warn('No lead provided to LeadStage component');
            return null;
        }

        const milestones = props.milestones.getMappingFor('entityId', lead.id);
        if (milestones.length === 0) {
            console.warn(`No milestones found for lead ${lead.id}`);
        }

        // Reverse sort the lead's milestones, by position
        const stages = milestones.filter(m => !!m.dateCompleted).sort((a, b) => b.position - a.position);

        // If no sales stage, the current is stage is AWAITING_INFO
        const hasStages = stages.length > 0;

        // Is this the WAITING_ON stage?  If so, special handling
        const isWaiting = hasStages ? MilestoneHelper.isWaitingOn(stages[0]) : false;

        // The current stage
        const stage = hasStages ? isWaiting ? stages[1] : stages[0] : null;

        // We only care about the position for now
        const position = stage?.position || MilestoneHelper.N_AWAITING_INFO;

        // If no sales stage, nothing to do.  If sales stage, return the correct label and coloration.
        if (!hasStages || !stage) {
            return <Tooltip title={milestoneDate ? 'on ' + DateUtil.formatDate(milestoneDate, true) : null}>
                <div key={`${lead.id}-${position}`}
                     className='u-ms--normal'
                     style={{backgroundColor: ChartProps.funnelColors[0]}}
                >
                    Awaiting Info
                </div>
            </Tooltip>;
        }

        let color = '';
        let className = 'u-ms--normal';

        if (MilestoneHelper.isNormalPipeline(stage)) {
            color = ChartProps.funnelColors[Math.floor(position - 1)];
        }
        else if (MilestoneHelper.isLost(stage)) className = className + ' u-ms--lost';
        else if (MilestoneHelper.isRefused(stage)) className = className + ' u-ms--refused';
        // else if (MilestoneHelper.isWaitingOn(stage)) className = className + ' u-ms--waiting-on';

        const lastDate = (milestoneDate && props.useDate) ? 'on ' + DateUtil.formatDate(milestoneDate, true) : null;
        const milestoneName = MilestoneHelper.getStages(position);

        const icon = isWaiting ? <FontAwesomeIcon color='black' icon={Icons.PARKING.reactIcon}/> : null;

        if (color) return <Tooltip title={'on ' + DateUtil.formatDate(milestoneDate, true)}>
            <div key={`${lead.id}-${position}`}
                 style={{backgroundColor: color}}
                 className={className}
            >
                {milestoneName} {lastDate} {icon}
            </div>
        </Tooltip>;
        else return <Tooltip title={'on ' + DateUtil.formatDate(milestoneDate, true)}>
            <div key={`${lead.id}-${position}`}
                 className={className}
            >
                {milestoneName} {lastDate}
            </div>
        </Tooltip>;
    }
    catch (err) {
        console.error(err);
        return <Help message={err.message} type='error' title='Error'/>;
    }
    /* eslint-enable react-hooks/error-boundaries */
}

export type LeadStageProps = Pick<RootState, 'milestones'> &  {
    // The lead to render
    lead?: Lead

    // Should we include the date?
    useDate?: boolean
};

const mapStateToProps = (state: RootState) => ({
    milestones: state.milestones
});

export default connect(mapStateToProps)(LeadStage);
